
import { useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import DiceRoller from "@/components/DiceRoller";
import { useSettings } from "@/context/SettingsContext";
import { useAuth } from "@/context/SafeAuthProvider";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { Brain, Dices, BookOpen, TrendingUp, ArrowRight, Sparkles, AlertTriangle } from "lucide-react";
import UnstuckLogo from "@/components/UnstuckLogo";

const Index = () => {
  const { darkMode } = useSettings();
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  // Redirect authenticated users to dashboard
  useEffect(() => {
    if (user && !loading) {
      navigate("/dashboard");
    }
  }, [user, loading, navigate]);

  // Don't render anything while checking authentication
  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6127A0]"></div>
    </div>;
  }

  // Only show to non-authenticated users
  if (user) {
    return null;
  }

  return (
    <div className={cn(
      "min-h-screen w-full",
      darkMode 
        ? "bg-gradient-to-br from-[#1a0930] via-[#2d1a45] to-[#1a0930]" 
        : "bg-gradient-to-br from-[#FBF3F1] via-white to-[#FBF3F1]"
    )}>

      {/* Hero Section */}
      <section className="relative px-4 py-16 md:py-24">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-8"
          >
            <UnstuckLogo textSize="lg" className="mb-6" />
            <h1 className={cn(
              "text-4xl md:text-6xl font-bold mb-6 leading-tight",
              darkMode ? "text-[#FBF3F1]" : "text-[#1E0C46]"
            )}>
              Break Through Mental Blocks with{" "}
              <span className={cn(
                "bg-gradient-to-r bg-clip-text text-transparent",
                darkMode 
                  ? "from-[#00FF99] to-[#AE78F0]" 
                  : "from-[#6127A0] to-[#D896A4]"
              )}>
                Dice-Driven Journaling
              </span>
            </h1>
            <p className={cn(
              "text-xl md:text-2xl mb-8 max-w-3xl mx-auto",
              darkMode ? "text-[#FBF3F1]/80" : "text-[#1E0C46]/80"
            )}>
              When you're stuck, sometimes you need to stop trying harder and start tuning in. 
              Let the dice guide you to what's already there.
            </p>
            <Button 
              onClick={() => navigate("/auth")}
              size="lg"
              className={cn(
                "px-8 py-4 text-lg rounded-full shadow-lg hover:shadow-xl transition-all duration-300",
                darkMode 
                  ? "bg-[#6127A0] hover:bg-[#4d1f80] text-white" 
                  : "bg-[#6127A0] hover:bg-[#4d1f80] text-white"
              )}
            >
              Start Your Journey <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="px-4 py-16">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className={cn(
              "text-3xl md:text-4xl font-bold mb-4",
              darkMode ? "text-[#FBF3F1]" : "text-[#1E0C46]"
            )}>
              How It Works
            </h2>
            <p className={cn(
              "text-lg max-w-2xl mx-auto",
              darkMode ? "text-[#FBF3F1]/70" : "text-[#1E0C46]/70"
            )}>
              A simple, intuitive process that helps you reconnect with your inner wisdom
            </p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              {
                icon: Brain,
                title: "Bring Your Challenge",
                description: "Think about what you're trying to do or where you feel stuck"
              },
              {
                icon: Dices,
                title: "Roll the Dice",
                description: "Let chance guide you to one of six awareness prompts"
              },
              {
                icon: BookOpen,
                title: "Follow the Prompts",
                description: "Journal through perception, intention, and next steps"
              },
              {
                icon: TrendingUp,
                title: "Find Your Path",
                description: "Discover insights and clarity to move forward"
              }
            ].map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className={cn(
                  "p-6 text-center h-full border-2 transition-all duration-300 hover:scale-105",
                  darkMode 
                    ? "bg-[#2d1a45]/50 border-[#6127A0]/30 text-[#FBF3F1] hover:border-[#00FF99]/50" 
                    : "bg-white/80 border-[#D896A4]/20 hover:border-[#6127A0]/50"
                )}>
                  <step.icon className={cn(
                    "h-12 w-12 mx-auto mb-4",
                    darkMode ? "text-[#00FF99]" : "text-[#6127A0]"
                  )} />
                  <h3 className={cn(
                    "text-lg font-semibold mb-2",
                    darkMode ? "text-[#FBF3F1]" : "text-[#1E0C46]"
                  )}>
                    {step.title}
                  </h3>
                  <p className={cn(
                    "text-sm",
                    darkMode ? "text-[#FBF3F1]/70" : "text-[#1E0C46]/70"
                  )}>
                    {step.description}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive Demo Section */}
      <section className="px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className={cn(
              "text-3xl md:text-4xl font-bold mb-4",
              darkMode ? "text-[#FBF3F1]" : "text-[#1E0C46]"
            )}>
              Try Unstuck Now
            </h2>
            <p className={cn(
              "text-lg max-w-2xl mx-auto mb-8",
              darkMode ? "text-[#FBF3F1]/70" : "text-[#1E0C46]/70"
            )}>
              Experience the magic yourself. Roll the dice below to see how prompts can shift your perspective.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Card className={cn(
              "p-8 shadow-2xl border-2",
              darkMode 
                ? "bg-[#2d1a45]/80 border-[#6127A0]/30" 
                : "bg-white/90 border-[#D896A4]/20"
            )}>
              <DiceRoller hideWelcomeText={true} />
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
            className="text-center mt-8"
          >
            <p className={cn(
              "text-sm mb-4",
              darkMode ? "text-[#FBF3F1]/60" : "text-[#1E0C46]/60"
            )}>
              This is just a preview. Sign up to save your journal entries and track your progress over time.
            </p>
            <Button 
              onClick={() => navigate("/auth")}
              variant="outline"
              className={cn(
                "border-2",
                darkMode 
                  ? "border-[#00FF99] text-[#00FF99] hover:bg-[#00FF99]/10" 
                  : "border-[#6127A0] text-[#6127A0] hover:bg-[#6127A0]/10"
              )}
            >
              Sign Up for Full Experience
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="px-4 py-16">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className={cn(
              "text-3xl md:text-4xl font-bold mb-4",
              darkMode ? "text-[#FBF3F1]" : "text-[#1E0C46]"
            )}>
              Why Dice-Driven Journaling Works
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: "🎯",
                title: "Breaks Analysis Paralysis",
                description: "Stop overthinking and start noticing what's already guiding you"
              },
              {
                icon: "💡",
                title: "Taps Intuitive Wisdom",
                description: "Access insights that logic alone can't reach"
              },
              {
                icon: "🎨",
                title: "Structured Yet Creative",
                description: "A framework that encourages authentic expression"
              },
              {
                icon: "📈",
                title: "Track Your Growth",
                description: "See patterns and progress in your journey over time"
              }
            ].map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className={cn(
                  "p-6 text-center h-full transition-all duration-300 hover:scale-105",
                  darkMode 
                    ? "bg-[#2d1a45]/30 border-[#6127A0]/20" 
                    : "bg-white/60 border-[#D896A4]/20"
                )}>
                  <div className="text-3xl mb-4">{benefit.icon}</div>
                  <h3 className={cn(
                    "text-lg font-semibold mb-2",
                    darkMode ? "text-[#FBF3F1]" : "text-[#1E0C46]"
                  )}>
                    {benefit.title}
                  </h3>
                  <p className={cn(
                    "text-sm",
                    darkMode ? "text-[#FBF3F1]/70" : "text-[#1E0C46]/70"
                  )}>
                    {benefit.description}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="px-4 py-16">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Sparkles className={cn(
              "h-12 w-12 mx-auto mb-6",
              darkMode ? "text-[#00FF99]" : "text-[#6127A0]"
            )} />
            <h2 className={cn(
              "text-3xl md:text-4xl font-bold mb-4",
              darkMode ? "text-[#FBF3F1]" : "text-[#1E0C46]"
            )}>
              Ready to Get Unstuck?
            </h2>
            <p className={cn(
              "text-lg mb-8 max-w-2xl mx-auto",
              darkMode ? "text-[#FBF3F1]/70" : "text-[#1E0C46]/70"
            )}>
              Join others who are breaking through mental blocks and discovering new possibilities. 
              Your journey of self-discovery starts with a single roll.
            </p>
            <div className="space-y-4">
              <Button 
                onClick={() => navigate("/auth")}
                size="lg"
                className={cn(
                  "px-8 py-4 text-lg rounded-full shadow-lg hover:shadow-xl transition-all duration-300",
                  darkMode 
                    ? "bg-[#6127A0] hover:bg-[#4d1f80] text-white" 
                    : "bg-[#6127A0] hover:bg-[#4d1f80] text-white"
                )}
              >
                Start Journaling Today <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <p className={cn(
                "text-sm",
                darkMode ? "text-[#FBF3F1]/60" : "text-[#1E0C46]/60"
              )}>
                Free to start • No credit card required
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-4 py-8 border-t border-opacity-20">
        <div className="max-w-6xl mx-auto text-center">
          <p className={cn(
            "text-sm",
            darkMode ? "text-[#FBF3F1]/40" : "text-[#1E0C46]/40"
          )}>
            Built with ❤️ by Amelia Stewart
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Index;
